/**
 * ArticleBody.jsx — the renderer for a long-form article's text.
 *
 * WHY THIS EXISTS AS ITS OWN COMPONENT
 *
 * The editor's preview and the published page have to be the same code. If the
 * preview is a separate implementation that merely looks similar, it will drift,
 * and the first time someone publishes a 2,000-word piece and finds the spacing
 * different from what they proofread, they stop trusting the preview — which
 * makes it worse than not having one.
 *
 * WHY NOT MARKDOWN, OR A RICH TEXT EDITOR
 *
 * Both mean user-authored markup reaching the DOM. A markdown library renders
 * raw HTML by default and every one of them has had an XSS advisory; a rich text
 * editor stores HTML, which then has to be sanitised on read, on write, and
 * again anywhere it is quoted or emailed. On a platform where a post can carry
 * clinical claims and a verified badge, an injected <a> or <img> is not a
 * cosmetic problem.
 *
 * So the format is plain text with four line-level conventions, parsed here into
 * React elements. Nothing is ever interpreted as HTML: the text goes through
 * <RichText>, which produces links and mentions as real React nodes from
 * tokens it recognises, and treats everything else as literal text.
 *
 *     ## Heading
 *     > Quotation
 *     - Bullet
 *     ![Description](link)   an image, on a line of its own
 *     Anything else is a paragraph. A blank line starts a new one.
 *
 * Images render only from Clincordant's own storage, and only from the author's own
 * folder there: any other address on an image line is shown as plain text. So
 * an article cannot carry a tracking pixel, a hotlinked picture that changes
 * after publication, or someone else's upload.
 *
 * That is deliberately about as much structure as a clinical write-up needs,
 * and the syntax is the same as the one most people have already met.
 */

import { useMemo, useState } from 'react';
import { RichText } from './ui';
import Lightbox from './Lightbox';
import SafeImage from './SafeImage';

const BUCKET = String(import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET || '').trim();
const IMAGE_LINE = /^!\[([^\]\n]{0,300})\]\((\S+)\)$/;

/**
 * An image address we will render: Firebase Storage, this project's bucket,
 * under posts/{authorId}/. Anything else is not an image as far as an article
 * is concerned.
 */
export function isArticleImageUrl(url, authorId) {
  let u;
  try { u = new URL(url); } catch { return false; }
  if (u.protocol !== 'https:' || u.hostname !== 'firebasestorage.googleapis.com') return false;
  const prefix = `/v0/b/${BUCKET}/o/`;
  if (!BUCKET || !u.pathname.startsWith(prefix)) return false;
  const object = decodeURIComponent(u.pathname.slice(prefix.length));
  if (!object.startsWith('posts/')) return false;
  if (authorId && !object.startsWith(`posts/${authorId}/`)) return false;
  return u.searchParams.get('alt') === 'media';
}

/**
 * Group raw text into blocks.
 *
 * Consecutive bullet lines become ONE list block rather than one list each,
 * which is the difference between a tidy list and five separate lists with
 * margin between them.
 */
function parseBlocks(text, authorId) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let paragraph = [];
  let bullets = [];

  const flushParagraph = () => {
    if (paragraph.length) blocks.push({ type: 'p', text: paragraph.join(' ') });
    paragraph = [];
  };
  const flushBullets = () => {
    if (bullets.length) blocks.push({ type: 'ul', items: bullets });
    bullets = [];
  };
  const flushAll = () => { flushParagraph(); flushBullets(); };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (!line.trim()) { flushAll(); continue; }

    const image = IMAGE_LINE.exec(line.trim());
    if (image && isArticleImageUrl(image[2], authorId)) {
      flushAll();
      blocks.push({ type: 'img', alt: image[1].trim(), url: image[2] });
      continue;
    }

    const heading = /^#{2,3}\s+(.*)$/.exec(line);
    if (heading) { flushAll(); blocks.push({ type: 'h', text: heading[1] }); continue; }

    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) { flushAll(); blocks.push({ type: 'quote', text: quote[1] }); continue; }

    const bullet = /^[-*]\s+(.*)$/.exec(line);
    if (bullet) { flushParagraph(); bullets.push(bullet[1]); continue; }

    // A plain line. Bullets end here; the paragraph continues, because a
    // paragraph wrapped across several lines in the editor is still one
    // paragraph — only a blank line ends it.
    flushBullets();
    paragraph.push(line.trim());
  }

  flushAll();
  return blocks;
}

export default function ArticleBody({ text, className = '', authorId }) {
  const blocks = useMemo(() => parseBlocks(text, authorId), [text, authorId]);
  const images = useMemo(() => blocks.filter((b) => b.type === 'img'), [blocks]);
  const [open, setOpen] = useState(null);

  return (
    <div className={`article-body ${className}`}>
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'img':
            return (
              <figure key={i} className="article-body__figure">
                <button
                  type="button"
                  className="article-body__imgbtn"
                  onClick={(e) => { e.stopPropagation(); setOpen(images.indexOf(block)); }}
                  aria-label={block.alt ? `Enlarge image: ${block.alt}` : 'Enlarge image'}
                >
                  <SafeImage src={block.url} alt={block.alt} loading="lazy" decoding="async" />
                </button>
                {block.alt && <figcaption>{block.alt}</figcaption>}
              </figure>
            );
          case 'h':
            return <h2 key={i} className="article-body__h">{block.text}</h2>;
          case 'quote':
            return (
              <blockquote key={i} className="article-body__quote">
                <RichText text={block.text} />
              </blockquote>
            );
          case 'ul':
            return (
              <ul key={i} className="article-body__ul">
                {block.items.map((item, j) => (
                  <li key={j}><RichText text={item} /></li>
                ))}
              </ul>
            );
          default:
            return (
              <p key={i} className="article-body__p">
                <RichText text={block.text} />
              </p>
            );
        }
      })}
      {open !== null && (
        <Lightbox images={images} index={open} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}

/**
 * A short plain-text excerpt, for the feed card and for meta descriptions.
 *
 * Strips the line markers so a card never shows a stray "## " or "- ", and cuts
 * on a word boundary — a excerpt that ends mid-word looks broken rather than
 * truncated.
 */
export function articleExcerpt(text, max = 220) {
  const flat = String(text || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    // Image lines are not words: an excerpt never shows "![...](https://...)".
    .filter((l) => !IMAGE_LINE.test(l.trim()))
    .map((l) => l.replace(/^(#{2,3}\s+|>\s?|[-*]\s+)/, '').trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > max * 0.6 ? lastSpace : max).trimEnd()}…`;
}

/**
 * Article typography.
 *
 * Measure is capped at 68 characters. Long-form set to the full width of a
 * desktop card is genuinely harder to read — the eye loses the line on the
 * return sweep — and it is the single clearest signal that nobody thought about
 * the reading experience.
 */
export const ARTICLE_CSS = `
.article-body { font-size: 1.0625rem; line-height: 1.75; color: var(--ink); }
.article-body > * + * { margin-top: 1.1em; }
.article-body__p { margin: 0; }
.article-body__h {
  margin: 1.9em 0 0; font-size: 1.1875rem; font-weight: 700;
  line-height: 1.35; letter-spacing: -0.01em; color: var(--ink);
}
.article-body__h:first-child { margin-top: 0; }
.article-body__ul { margin: 0; padding-left: 1.3em; }
.article-body__ul li { margin: 0.35em 0; }
.article-body__quote {
  margin: 1.4em 0; padding: 2px 0 2px 16px;
  border-left: 3px solid var(--primary);
  color: var(--ink-soft); font-style: normal;
}

/* ── Article header, shared by the detail page and the editor preview ─────── */

.article-head { margin-bottom: 20px; }
.article-head__title {
  margin: 0 0 10px; font-size: clamp(1.5rem, 1.1rem + 1.9vw, 2.125rem);
  line-height: 1.2; letter-spacing: -0.02em; font-weight: 700; color: var(--ink);
  /* A 140-character title with no spaces would otherwise push the page wide. */
  overflow-wrap: anywhere;
}
.article-head__meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  font-size: var(--text-sm); color: var(--ink-faint);
}
.article-head__dot { opacity: 0.6; }

/* The cover sits ABOVE the title and never behind it. Text over a photograph
   is unreadable at some point on some image, and there is no way to know which
   image in advance — so it is never done. */
.article-cover {
  display: block; width: 100%; aspect-ratio: 2 / 1; object-fit: cover;
  border-radius: var(--radius); background: var(--bg-alt);
  margin-bottom: 18px;
}

/* Reading width. Applied to the article column on the detail page only; the
   feed card is already narrower than this. */
.article-measure { max-width: 68ch; }
`;
